/**
 * Shared logic for recent files across file tree sidebar and command palette.
 */

export interface RecentFileEntry {
  path: string;
  lastAccessedAt: number;
}

/**
 * Filters recent files to only those that exist in the current project.
 */
export function filterRecentFilesByExisting(
  recentFiles: RecentFileEntry[],
  existingPaths: Set<string>,
): RecentFileEntry[] {
  return recentFiles.filter((e) => existingPaths.has(e.path));
}

/**
 * Returns the most recent file path that still exists, if any.
 */
export function getMostRecentExistingFile(
  recentFiles: RecentFileEntry[],
  existingPaths: Set<string>,
): string | null {
  return (
    filterRecentFilesByExisting(recentFiles, existingPaths)[0]?.path ?? null
  );
}

/**
 * Returns a friendly relative time string (e.g. "5 minutes ago", "yesterday").
 */
export function formatFriendlyTime(timestamp: number): string {
  if (timestamp <= 0) return "Unknown";
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) return "just now";
  if (diffMin === 1) return "a minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;
  if (diffHr === 1) return "an hour ago";
  if (diffHr < 24) return `${diffHr} hours ago`;
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffWeek === 1) return "a week ago";
  if (diffWeek < 4) return `${diffWeek} weeks ago`;
  if (diffMonth === 1) return "a month ago";
  if (diffMonth < 12) return `${diffMonth} months ago`;
  if (diffYear === 1) return "a year ago";
  return `${diffYear} years ago`;
}

/**
 * Returns an exact date/time string for tooltips.
 */
export function formatExactDateTime(timestamp: number): string {
  if (timestamp <= 0) return "Unknown";
  return new Date(timestamp).toLocaleString();
}

/**
 * Parses a file path for display into fileName (basename) and dirPath (parent directory).
 */
export function parseFilePathDisplay(filePath: string): {
  fileName: string;
  dirPath: string;
} {
  const fileName = filePath.split("/").pop() ?? filePath;
  const dirPath = filePath.includes("/")
    ? filePath.slice(0, filePath.lastIndexOf("/"))
    : "";
  return { fileName, dirPath };
}
